import { z } from "zod";
import { Format, type Brief } from "./brief.js";
import { namesIn } from "./article.js";
import { planFor, VOICES } from "./plan.js";
import { PHOTOS_OFFERED } from "./studioTurn.js";

/**
 * Prepare my week ([ADR 0023](../../../docs/adr/0023-a-prepared-week-approved-by-a-person-paid-as-it-is-made.md)
 * point 2, [03 §prepare my week](../../../docs/03-studio-flow.md#prepare-my-week)).
 *
 * One call reads the business page and the photo library and proposes a week of posts, one a day at most. Code
 * decides what survives it, as it does for every other model answer here (ADR 0031): a photo it was not offered is
 * dropped, a day outside the week or taken twice is dropped, a second video is dropped, and a piece that names
 * somebody the business never names is dropped. What survives becomes Studio drafts with a day on them — **a plan is
 * a draft with a date**, so the chat, Generate and delete all work on it unchanged. Planning is the only thing that
 * is paid for here; making each piece is Generate, priced on the button, as it always was.
 */

/** A week is seven days, from tomorrow. */
export const WEEK_DAYS = 7;
/** How many pieces a week holds: enough to be a week, few enough to be read on a Monday morning. */
export const WEEK_PIECES = 5;
/**
 * At most one video, because a video is where the money is ([04](../../../docs/04-providers-and-cost.md)): a week of
 * posts is cents, a week of videos made from photos is dollars, and nobody asked for dollars by pressing one button.
 */
export const WEEK_VIDEOS = 1;
/** The hour a planned piece is for, in the workspace's own day. The owner moves it when it is scheduled. */
export const WEEK_HOUR = 10;

/** What `plan_week` is given. Photos are numbered, as the Studio guide's are, and picked back by number. */
export const WeekPlanInput = z.object({
  business: z.string().default(""),
  photos: z.array(z.object({ number: z.number().int().min(1), caption: z.string(), from: z.string() })).max(PHOTOS_OFFERED).default([]),
  /** What was made or planned lately, so the week does not say it again. */
  recent: z.array(z.string()).max(60).default([]),
  /** The seven days, in words, day 1 first: "Saturday 27 September". */
  days: z.array(z.string()).length(WEEK_DAYS),
  pieces: z.number().int().min(1).max(WEEK_DAYS).default(WEEK_PIECES),
  videos: z.number().int().min(0).max(WEEK_DAYS).default(WEEK_VIDEOS),
});
export type WeekPlanInput = z.infer<typeof WeekPlanInput>;

/**
 * One piece as the model proposes it. No length limits in the schema, as with the website (`website.ts`): a
 * structured answer that runs over a limit fails whole, and a week lost for one long sentence is worse than a
 * sentence trimmed by code.
 */
export const WeekPiece = z.object({
  /** 1 to 7, as numbered in `days`. */
  day: z.number().int(),
  /** What the post is about, in the owner's words: the sentence they would have typed into the Studio. */
  idea: z.string(),
  kind: z.enum(["post", "video"]),
  format: Format,
  /** Photo numbers from the list given. Empty means none fits. */
  photos: z.array(z.number().int()),
  /** Words on screen: an offer, a time, a line. Empty for none. */
  onScreenText: z.string(),
  /** One line on why this, this day — shown to the owner beside the piece. */
  why: z.string(),
});
export type WeekPiece = z.infer<typeof WeekPiece>;

export const WeekPlan = z.object({ pieces: z.array(WeekPiece) });
export type WeekPlan = z.infer<typeof WeekPlan>;

/** A piece code has accepted: a complete brief, a day, and the line on why. */
export interface PlannedPiece {
  planFor: Date;
  draft: Brief;
  /** Every part of the brief is Joogoos's suggestion until the owner changes it (ADR 0031): all of them, here. */
  proposed: Array<keyof Brief>;
  /** One line on why this, this day. It opens the piece's chat, which is where the owner reads it before Generate. */
  why: string;
}

/** Why a piece was dropped, for the log. Never shown: the owner sees the week that survived. */
export type WeekRefusal = "day" | "same_day" | "idea" | "named" | "second_video" | "too_many";

const IDEA_MAX = 500;
const TEXT_MAX = 120;
const WHY_MAX = 200;
const POST_PHOTOS = 1;
const VIDEO_PHOTOS = 6;
const VIDEO_MS = 15_000;

/** The day a week starts: tomorrow, at midnight, in this process's own day (the workspace's, `tabs.ts`). */
export function weekStart(now: Date): Date {
  const d = new Date(now);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 1);
  return d;
}

/** The seven days as the model is told them. */
export function weekDays(start: Date): string[] {
  return Array.from({ length: WEEK_DAYS }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" });
  });
}

/**
 * What survives the model's week. Nothing here trusts the answer: the photos are the ones offered, by number; the
 * days are the seven given, once each; one video at most; and nothing names what the business never names.
 */
export function applyWeek(
  plan: WeekPlan,
  ctx: { offered: readonly string[]; start: Date; neverName: readonly string[]; pieces?: number; videos?: number },
): { pieces: PlannedPiece[]; refused: WeekRefusal[] } {
  const most = ctx.pieces ?? WEEK_PIECES;
  const videosMost = ctx.videos ?? WEEK_VIDEOS;
  const refused: WeekRefusal[] = [];
  const pieces: PlannedPiece[] = [];
  const taken = new Set<number>();
  let videos = 0;

  for (const p of [...plan.pieces].sort((a, b) => a.day - b.day)) {
    if (!Number.isInteger(p.day) || p.day < 1 || p.day > WEEK_DAYS) { refused.push("day"); continue; }
    if (taken.has(p.day)) { refused.push("same_day"); continue; }
    const idea = p.idea.trim().slice(0, IDEA_MAX);
    if (idea.length < 3) { refused.push("idea"); continue; }
    const text = p.onScreenText.trim().slice(0, TEXT_MAX);
    if (namesIn(`${idea}\n${text}`, [...ctx.neverName]).length) { refused.push("named"); continue; }
    if (p.kind === "video" && videos >= videosMost) { refused.push("second_video"); continue; }
    if (pieces.length >= most) { refused.push("too_many"); continue; }

    const ids = [...new Set(p.photos.flatMap((n) => (Number.isInteger(n) && ctx.offered[n - 1] ? [ctx.offered[n - 1]!] : [])))]
      .slice(0, p.kind === "post" ? POST_PHOTOS : VIDEO_PHOTOS);
    const draft: Brief = p.kind === "post"
      ? { idea, kind: "post", format: p.format, sourceMediaIds: ids, voice: { enabled: false } }
      : {
          idea, kind: "video", format: p.format, sourceMediaIds: ids, voice: { enabled: true, voiceId: VOICES[0]!.id },
          animation: { enabled: false }, durationMs: VIDEO_MS,
          // A video with no photo to make it from is drawn: its scenes cost nothing, where generated pictures and their
          // motion are the most expensive thing this product does (ADR 0050).
          ...(ids.length ? { look: "pictures" as const } : { look: "drawn" as const, style: "clean" as const }),
        };
    if (text) draft.onScreenText = text;

    const planFor = new Date(ctx.start);
    planFor.setDate(ctx.start.getDate() + p.day - 1);
    planFor.setHours(WEEK_HOUR, 0, 0, 0);
    if (p.kind === "video") videos++;
    taken.add(p.day);
    pieces.push({ planFor, draft, proposed: Object.keys(draft) as Array<keyof Brief>, why: p.why.trim().slice(0, WHY_MAX) });
  }
  return { pieces, refused };
}

/** What making every piece is expected to cost, in dollars: the sum of the plans Generate would show one by one. */
export function weekTotalUsd(drafts: readonly Brief[]): string {
  return drafts.reduce((n, d) => n + Number(planFor(d).totalUsd), 0).toFixed(2);
}

/** A plan is a draft with a day on it and nothing made from it yet. */
export const isPlanned = (p: { status: string; planFor: string | Date | null }) => p.status === "chatting" && p.planFor !== null;

/* ── the API ─────────────────────────────────────────────────────────────────────────────────────────────── */

/** One planned piece as the week screen draws it. */
export interface WeekItem {
  projectId: string;
  title: string;
  kind: "post" | "video";
  planFor: string;
  /** What Generate would charge, from the same table the plan shows (`planFor`). */
  usd: string;
  /** Whether the brief is whole: a plan whose photo was deleted since, say, has to be opened first. */
  ready: boolean;
}

/** `GET /v1/week` */
export interface WeekResponse {
  items: WeekItem[];
  /** Making every piece that is ready, in dollars. */
  totalUsd: string;
  /** What planning a week costs, estimated before the button. Null when nothing is configured to plan it. */
  planUsd: string | null;
}

/** `POST /v1/week/make`: what was started, and what was left because it was not whole. */
export interface WeekMadeResponse {
  started: string[];
  skipped: string[];
}
